import * as React from "react";
import { useState } from "react";
import { Box, Button, FormControl, InputLabel, MenuItem, Select, TextField, Typography } from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ptBR } from "date-fns/locale";
import GenericDialog from "../../../../../../components/dialog/generic-dialog.component";
import { translate } from "../../../../../../translate/translate";
import { NpiHolidayService, HolidayRangePayload } from "../../../../../../services/npi-holiday-service";
import { ErrorComponent } from "../../../../../../components/error/error.component";
import { toast } from "react-toastify";

interface NpiAddHolidayDialogProps {
    open: boolean;
    defaultCountry: "BRASIL" | "CHINA";
    onClose: () => void;
    onSaved: () => void;
}

const toIsoDate = (date: Date | null): string | undefined => {
    if (!date || isNaN(date.getTime())) {
        return undefined;
    }
    return date.toISOString().split("T")[0];
};

const NpiAddHolidayDialogComponent: React.FC<NpiAddHolidayDialogProps> = ({ open, defaultCountry, onClose, onSaved }) => {
    const holidayService = React.useMemo(() => new NpiHolidayService(), []);

    const [country, setCountry] = useState<"BRASIL" | "CHINA">(defaultCountry);
    const [description, setDescription] = useState("");
    const [scope, setScope] = useState<"national" | "MANAUS" | "SAO_PAULO" | "">("");
    const [holidayType, setHolidayType] = useState<"feriado" | "tiaoxiu" | "">("");
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);

    React.useEffect(() => {
        if (open) {
            setCountry(defaultCountry);
            setDescription("");
            setScope("");
            setHolidayType("");
            setStartDate(null);
            setEndDate(null);
        }
    }, [open, defaultCountry]);

    const isEndDateValid = (
        !endDate ||
        (!isNaN(endDate.getTime()) && !!startDate && !isNaN(startDate.getTime()) && endDate.getTime() >= startDate.getTime())
    );

    const isValid = (
        description.trim() !== "" &&
        !!toIsoDate(startDate) &&
        isEndDateValid &&
        (country === "CHINA" || scope !== "") &&
        (country === "BRASIL" || holidayType !== "")
    );

    const handleConfirm = () => {
        if (!isValid) {
            return;
        }

        const payload: HolidayRangePayload = {
            description: description.trim(),
            country,
            start_date: toIsoDate(startDate)!,
            end_date: toIsoDate(endDate),
        };
        if (country === "BRASIL") {
            payload.scope = scope as "national" | "MANAUS" | "SAO_PAULO";
        }
        if (country === "CHINA") {
            payload.holiday_type = holidayType as "feriado" | "tiaoxiu";
        }

        holidayService.create(payload as any).then(() => {
            toast.success(translate.t("successfully_registered"));
            onSaved();
            onClose();
        }).catch((error: any) => {
            ErrorComponent(error);
        });
    };

    return (
        <GenericDialog
            open={open}
            onClose={onClose}
            title={"add_holiday"}
            colorButton={"primary"}
            labelButton={"save_form"}
            closeButtonText={"cancel"}
            disableConfirm={!isValid}
            handleConfirm={handleConfirm}
        >
            <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                <Button
                    fullWidth
                    variant={country === "BRASIL" ? "contained" : "outlined"}
                    onClick={() => setCountry("BRASIL")}
                >
                    🇧🇷 {translate.t("brasil")}
                </Button>
                <Button
                    fullWidth
                    variant={country === "CHINA" ? "contained" : "outlined"}
                    onClick={() => setCountry("CHINA")}
                >
                    🇨🇳 {translate.t("china")}
                </Button>
            </Box>

            <TextField
                label={`${translate.t("description")}*`}
                fullWidth
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                sx={{ mb: 2 }}
                inputProps={{ maxLength: 256 }}
            />

            {country === "BRASIL" && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="holiday-scope-label">{`${translate.t("resource")}*`}</InputLabel>
                    <Select
                        labelId="holiday-scope-label"
                        label={`${translate.t("resource")}*`}
                        value={scope}
                        onChange={(event) => setScope(event.target.value as any)}
                    >
                        <MenuItem value="national">{translate.t("national")}</MenuItem>
                        <MenuItem value="MANAUS">Manaus</MenuItem>
                        <MenuItem value="SAO_PAULO">São Paulo</MenuItem>
                    </Select>
                </FormControl>
            )}

            {country === "CHINA" && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="holiday-type-label">{`${translate.t("resource")}*`}</InputLabel>
                    <Select
                        labelId="holiday-type-label"
                        label={`${translate.t("resource")}*`}
                        value={holidayType}
                        onChange={(event) => setHolidayType(event.target.value as "feriado" | "tiaoxiu")}
                    >
                        <MenuItem value="feriado">{translate.t("feriado")}</MenuItem>
                        <MenuItem value="tiaoxiu">{translate.t("tiaoxiu")}</MenuItem>
                    </Select>
                </FormControl>
            )}

            <Box sx={{ display: "flex", gap: 2 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                    <DatePicker
                        label={`${translate.t("start_date")}*`}
                        value={startDate}
                        onChange={(date) => setStartDate(date as Date)}
                        slotProps={{ textField: { fullWidth: true, error: !!startDate && isNaN(startDate.getTime()) } }}
                    />
                </LocalizationProvider>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                    <DatePicker
                        label={translate.t("end_date")}
                        value={endDate}
                        minDate={startDate ?? undefined}
                        onChange={(date) => setEndDate(date as Date)}
                        slotProps={{ textField: { fullWidth: true, error: !isEndDateValid } }}
                    />
                </LocalizationProvider>
            </Box>
        </GenericDialog>
    );
};

export default NpiAddHolidayDialogComponent;